import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { translateError } from "../utils/i18nErrors";
import { Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Google Login oculto temporalmente: el Client ID de Google Cloud está roto a
// nivel de configuración (401 invalid_client), no del código. Cambiar a `true`
// una vez que se reconfigure un Client ID válido en GCP — el resto del código
// (GoogleOAuthProvider, GoogleLogin, /auth/google) queda intacto y reversible.
const GOOGLE_LOGIN_ENABLED = false;

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "candidate" });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [googleRoleModal, setGoogleRoleModal] = useState(false);
  const { login, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(user.role === "employer" ? "/company/dashboard" : "/dashboard");
  }, [user, navigate]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (tab === "register" && form.password.length < 8) {
      setError(t("auth.passwordTooShort"));
      return;
    }
    setLoading(true);
    try {
      if (tab === "login") {
        const res = await axios.post(`${API}/auth/login`, { email: form.email, password: form.password });
        login(res.data.token, res.data.user);
        navigate(res.data.user.role === "employer" ? "/company/dashboard" : "/dashboard");
      } else {
        if (!form.name.trim()) throw new Error("Name is required");
        const res = await axios.post(`${API}/auth/register`, {
          name: form.name, email: form.email, password: form.password, role: form.role,
          terms_accepted: termsAccepted, marketing_email_opt_in: marketingOptIn,
        });
        login(res.data.token, res.data.user);
        navigate(res.data.user.role === "employer" ? "/company/dashboard" : "/dashboard");
      }
    } catch (err) {
      setError(translateError(err.response?.data?.detail || "", t) || t("errors.unknownError"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credentialResponse, role) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/google`, { credential: credentialResponse.credential, role });
      login(res.data.token, res.data.user);
      navigate(res.data.user.role === "employer" ? "/company/dashboard" : "/dashboard");
    } catch (err) {
      setError(translateError(err.response?.data?.detail || "", t) || t("errors.unknownError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="auth-page-shell bg-gradient-to-br from-slate-50 to-white flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Navbar (desktop) / MobileHeader (mobile) already show the InfoCityJobs
            logo — no need to duplicate it here, just the welcome message. */}
        <div className="text-center mb-6">
          <p className="text-slate-500 text-sm">
            {tab === "login" ? t("auth.welcomeBack") : t("auth.joinUs")}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
          {/* Tabs */}
          <div className="flex rounded-xl bg-slate-100 p-1 mb-6" data-testid="auth-tabs">
            {["login", "register"].map((t2) => (
              <button key={t2} data-testid={`tab-${t2}`} onClick={() => { setTab(t2); setError(""); }}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all capitalize ${tab === t2 ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                {t2 === "login" ? t("auth.signIn") : t("auth.register")}
              </button>
            ))}
          </div>

          {/* Google Auth — oculto (ver GOOGLE_LOGIN_ENABLED arriba) */}
          {GOOGLE_LOGIN_ENABLED && (
          <div className="mb-5">
            {!googleRoleModal ? (
              <button data-testid="google-login-button" onClick={() => setGoogleRoleModal(true)}
                className="w-full flex items-center justify-center gap-3 border border-slate-200 rounded-xl py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
                {t("auth.continueGoogle")}
              </button>
            ) : (
              <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-medium text-slate-700 text-center mb-1">{t("auth.signInAs")}</p>
                <div data-testid="google-candidate-button" className="flex flex-col items-center">
                  <p className="text-xs text-slate-500 mb-1.5 text-center">{t("auth.signInCandidate")}</p>
                  <div className="flex justify-center w-full">
                    <GoogleLogin
                      onSuccess={(cred) => handleGoogleCredential(cred, "candidate")}
                      onError={() => setError(t("errors.googleLoginFailed"))}
                      text="continue_with" shape="pill" width="280"
                    />
                  </div>
                </div>
                <div data-testid="google-employer-button" className="flex flex-col items-center">
                  <p className="text-xs text-slate-500 mb-1.5 text-center">{t("auth.signInEmployer")}</p>
                  <div className="flex justify-center w-full">
                    <GoogleLogin
                      onSuccess={(cred) => handleGoogleCredential(cred, "employer")}
                      onError={() => setError(t("errors.googleLoginFailed"))}
                      text="continue_with" shape="pill" width="280"
                    />
                  </div>
                </div>
                <button onClick={() => setGoogleRoleModal(false)} className="w-full text-xs text-slate-400 hover:text-slate-600 pt-1">
                  {t("auth.cancel")}
                </button>
              </div>
            )}
          </div>
          )}

          {GOOGLE_LOGIN_ENABLED && (
          <div className="relative flex items-center mb-5">
            <div className="flex-1 border-t border-slate-200" />
            <span className="mx-3 text-xs text-slate-400 bg-white">{t("auth.orEmail")}</span>
            <div className="flex-1 border-t border-slate-200" />
          </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} data-testid="auth-form" className="space-y-4">
            {tab === "register" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("auth.fullNameLabel")}</label>
                <input data-testid="name-input" name="name" value={form.name} onChange={handleChange}
                  placeholder={t("auth.namePlaceholder")} required
                  className="w-full h-11 rounded-lg border border-slate-200 px-4 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/30 focus:border-[#028CF8] transition-all" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("auth.emailLabel")}</label>
              <input data-testid="email-input" name="email" type="email" value={form.email} onChange={handleChange}
                placeholder={t("auth.emailPlaceholder")} required
                className="w-full h-11 rounded-lg border border-slate-200 px-4 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/30 focus:border-[#028CF8] transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("auth.passwordLabel")}</label>
              <div className="relative">
                <input data-testid="password-input" name="password" type={showPassword ? "text" : "password"}
                  value={form.password} onChange={handleChange}
                  placeholder={t("auth.passwordPlaceholder")} required
                  className="w-full h-11 rounded-lg border border-slate-200 px-4 pr-11 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/30 focus:border-[#028CF8] transition-all" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {tab === "login" && (
                <div className="text-right mt-1.5">
                  <button type="button" data-testid="forgot-password-link" onClick={() => navigate("/forgot-password")}
                    className="text-xs text-[#028CF8] font-medium hover:underline">
                    {t("auth.forgotPassword")}
                  </button>
                </div>
              )}
            </div>
            {tab === "register" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t("auth.iAm")}</label>
                <div className="grid grid-cols-2 gap-3" data-testid="role-selector">
                  {[{ value: "candidate", label: t("auth.jobSeeker"), icon: "👤" }, { value: "employer", label: t("auth.employer"), icon: "🏢" }].map(({ value, label, icon }) => (
                    <label key={value} data-testid={`role-${value}`}
                      className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer transition-all ${form.role === value ? "border-[#028CF8] bg-[#028CF8]/5 text-[#028CF8]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                      <input type="radio" name="role" value={value} checked={form.role === value} onChange={handleChange} className="sr-only" />
                      <span>{icon}</span>
                      <span className="text-sm font-medium">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {tab === "register" && (
              <div className="space-y-2.5">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" required data-testid="terms-accept-checkbox"
                    checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#028CF8] focus:ring-[#028CF8]/30 flex-shrink-0" />
                  <span className="text-xs text-slate-600 leading-relaxed">
                    {t("auth.acceptTermsPrefix")}{" "}
                    <Link to="/terms" target="_blank" data-testid="terms-link" className="text-[#028CF8] font-medium hover:underline">{t("auth.termsOfService")}</Link>
                    {" "}{t("auth.acceptTermsAnd")}{" "}
                    <Link to="/privacy" target="_blank" data-testid="privacy-link" className="text-[#028CF8] font-medium hover:underline">{t("auth.privacyPolicy")}</Link>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" data-testid="marketing-optin-checkbox"
                    checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#028CF8] focus:ring-[#028CF8]/30 flex-shrink-0" />
                  <span className="text-xs text-slate-500 leading-relaxed">{t("auth.marketingOptIn")}</span>
                </label>
              </div>
            )}

            {error && (
              <div data-testid="auth-error" className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button type="submit" data-testid="auth-submit-button" disabled={loading}
              className="w-full h-11 btn-primary justify-center rounded-xl text-sm disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : tab === "login" ? t("auth.signIn") : t("auth.createAccount")}
            </button>
          </form>
          <p className="text-center text-xs text-slate-500 mt-4">
            {tab === "login" ? (
              <>{t("auth.noAccount")}{" "}<button onClick={() => setTab("register")} className="text-[#028CF8] font-medium hover:underline">{t("auth.register")}</button></>
            ) : (
              <>{t("auth.alreadyHave")}{" "}<button onClick={() => setTab("login")} className="text-[#028CF8] font-medium hover:underline">{t("auth.signIn")}</button></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
